import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Chip,
  Skeleton,
  FormControl,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Calendar,
  AlertTriangle,
  Target,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { Project, ProjectStatus, Milestone, TaskPriority } from '@/types';

dayjs.extend(isBetween);

interface ProjectGanttChartProps {
  projects: Project[];
  isLoading: boolean;
  onProjectClick?: (projectId: string) => void;
}

type ViewMode = 'day' | 'week' | 'month';

// Status colors
const statusColors: Record<ProjectStatus, string> = {
  [ProjectStatus.PLANNING]: '#64748b',
  [ProjectStatus.ACTIVE]: '#2563eb',
  [ProjectStatus.ON_HOLD]: '#f59e0b',
  [ProjectStatus.COMPLETED]: '#16a34a',
  [ProjectStatus.CANCELLED]: '#ef4444',
};

// Priority colors (using TaskPriority enum values)
const priorityColors: Record<TaskPriority | string, string> = {
  [TaskPriority.P1]: '#ef4444', // Critical
  [TaskPriority.P2]: '#f59e0b', // High
  [TaskPriority.P3]: '#2563eb', // Medium
  [TaskPriority.P4]: '#64748b', // Low
  // Fallbacks for string values
  critical: '#ef4444',
  high: '#f59e0b',
  medium: '#2563eb',
  low: '#64748b',
};

/**
 * ProjectGanttChart
 * Timeline visualization for project schedules
 * Shows project duration, milestones, and dependencies
 */
const ProjectGanttChart: React.FC<ProjectGanttChartProps> = ({
  projects,
  isLoading,
  onProjectClick,
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf('month'));

  // Calculate the date range to display
  const { dateRange, columnWidth, totalDays } = useMemo(() => {
    let days: number;
    let width: number;

    switch (viewMode) {
      case 'day':
        days = 30;
        width = 60;
        break;
      case 'week':
        days = 90;
        width = 30;
        break;
      case 'month':
        days = 365;
        width = 8;
        break;
      default:
        days = 90;
        width = 30;
    }

    const range: Dayjs[] = [];
    for (let i = 0; i < days; i++) {
      range.push(startDate.add(i, 'day'));
    }

    return { dateRange: range, columnWidth: width, totalDays: days };
  }, [viewMode, startDate]);

  // Scroll to today on mount
  useEffect(() => {
    if (scrollRef.current) {
      const today = dayjs();
      const daysFromStart = today.diff(startDate, 'day');
      if (daysFromStart > 0 && daysFromStart < totalDays) {
        scrollRef.current.scrollLeft = daysFromStart * columnWidth - 200;
      }
    }
  }, [startDate, columnWidth, totalDays]);

  // Navigate timeline
  const handlePrevious = () => {
    switch (viewMode) {
      case 'day':
        setStartDate(startDate.subtract(15, 'day'));
        break;
      case 'week':
        setStartDate(startDate.subtract(1, 'month'));
        break;
      case 'month':
        setStartDate(startDate.subtract(3, 'month'));
        break;
    }
  };

  const handleNext = () => {
    switch (viewMode) {
      case 'day':
        setStartDate(startDate.add(15, 'day'));
        break;
      case 'week':
        setStartDate(startDate.add(1, 'month'));
        break;
      case 'month':
        setStartDate(startDate.add(3, 'month'));
        break;
    }
  };

  const handleToday = () => {
    setStartDate(dayjs().startOf('month'));
  };

  const handleProjectClick = (projectId: string) => {
    if (onProjectClick) {
      onProjectClick(projectId);
    } else {
      navigate(`/projects/${projectId}`);
    }
  };

  // Calculate bar position for a project
  const getBarPosition = (project: Project) => {
    const projectStart = dayjs(project.startDate);
    const projectEnd = dayjs(project.endDate);
    const rangeStart = startDate;
    const rangeEnd = startDate.add(totalDays, 'day');

    // Check if project is in range
    if (projectEnd.isBefore(rangeStart) || projectStart.isAfter(rangeEnd)) {
      return null;
    }

    const effectiveStart = projectStart.isBefore(rangeStart)
      ? rangeStart
      : projectStart;
    const effectiveEnd = projectEnd.isAfter(rangeEnd) ? rangeEnd : projectEnd;

    const left = effectiveStart.diff(rangeStart, 'day') * columnWidth;
    const width = Math.max(effectiveEnd.diff(effectiveStart, 'day') * columnWidth, 20);

    return { left, width };
  };

  // Get milestone position
  const getMilestonePosition = (milestone: Milestone) => {
    const milestoneDate = dayjs(milestone.dueDate);
    const rangeStart = startDate;
    const rangeEnd = startDate.add(totalDays, 'day');

    if (milestoneDate.isBefore(rangeStart) || milestoneDate.isAfter(rangeEnd)) {
      return null;
    }

    const left = milestoneDate.diff(rangeStart, 'day') * columnWidth;
    return left;
  };

  // Group dates for header
  const getGroupedDates = () => {
    const groups: { label: string; span: number; dates: Dayjs[] }[] = [];
    let currentGroup: { label: string; span: number; dates: Dayjs[] } | null = null;

    dateRange.forEach((date) => {
      let label: string;
      switch (viewMode) {
        case 'day':
          label = date.format('MMM YYYY');
          break;
        case 'week':
          label = date.format('MMM YYYY');
          break;
        case 'month':
          label = date.format('YYYY');
          break;
        default:
          label = date.format('MMM YYYY');
      }

      if (!currentGroup || currentGroup.label !== label) {
        currentGroup = { label, span: 1, dates: [date] };
        groups.push(currentGroup);
      } else {
        currentGroup.span++;
        currentGroup.dates.push(date);
      }
    });

    return groups;
  };

  const groupedDates = getGroupedDates();

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  // Filter and sort projects
  const sortedProjects = [...projects].sort(
    (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevious} size="small">
            <ChevronLeft size={20} />
          </IconButton>
          <Button variant="outlined" size="small" onClick={handleToday}>
            Today
          </Button>
          <IconButton onClick={handleNext} size="small">
            <ChevronRight size={20} />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {startDate.format('MMM YYYY')} -{' '}
            {startDate.add(totalDays, 'day').format('MMM YYYY')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* View Mode */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as ViewMode)}
            >
              <MenuItem value="day">Days</MenuItem>
              <MenuItem value="week">Weeks</MenuItem>
              <MenuItem value="month">Months</MenuItem>
            </Select>
          </FormControl>

          {/* Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#16a34a',
                }}
              />
              <Typography variant="caption">Completed</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#2563eb',
                }}
              />
              <Typography variant="caption">Active</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Target size={12} style={{ color: '#9333ea' }} />
              <Typography variant="caption">Milestone</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Gantt Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Project List (Fixed) */}
        <Box
          sx={{
            width: 280,
            minWidth: 280,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              height: 64,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Projects ({sortedProjects.length})
            </Typography>
          </Box>

          {/* Project Rows */}
          {sortedProjects.map((project) => (
            <Box
              key={project.id}
              onClick={() => handleProjectClick(project.id)}
              sx={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                gap: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'grey.50',
                },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: statusColors[project.status],
                }}
              />
              <Tooltip title={project.name}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ flex: 1, fontWeight: 500 }}
                >
                  {project.name}
                </Typography>
              </Tooltip>
              <Chip
                label={project.priority}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  textTransform: 'capitalize',
                  bgcolor: `${priorityColors[project.priority]}15`,
                  color: priorityColors[project.priority],
                }}
              />
              {project.riskLevel === 'high' && (
                <Tooltip title="High Risk">
                  <AlertTriangle size={14} color="#dc2626" />
                </Tooltip>
              )}
            </Box>
          ))}

          {sortedProjects.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No projects to display
              </Typography>
            </Box>
          )}
        </Box>

        {/* Timeline (Scrollable) */}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          {/* Date Headers */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
            }}
          >
            {/* Month/Year Row */}
            <Box
              sx={{
                display: 'flex',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              {groupedDates.map((group, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: group.span * columnWidth,
                    minWidth: group.span * columnWidth,
                    px: 1,
                    py: 0.5,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {group.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Day Row */}
            <Box
              sx={{
                display: 'flex',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {dateRange.map((date, idx) => {
                const isToday = date.isSame(dayjs(), 'day');
                const isWeekend = date.day() === 0 || date.day() === 6;

                return (
                  <Box
                    key={idx}
                    sx={{
                      width: columnWidth,
                      minWidth: columnWidth,
                      py: 0.5,
                      textAlign: 'center',
                      bgcolor: isToday
                        ? 'primary.50'
                        : isWeekend
                        ? 'grey.100'
                        : 'transparent',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: viewMode === 'month' ? '0.6rem' : '0.7rem',
                        fontWeight: isToday ? 600 : 400,
                        color: isToday ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {viewMode === 'day'
                        ? date.format('D')
                        : viewMode === 'week'
                        ? date.format('D')
                        : date.date() === 1
                        ? date.format('MMM')
                        : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Gantt Bars */}
          <Box sx={{ position: 'relative' }}>
            {/* Grid Lines */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                pointerEvents: 'none',
              }}
            >
              {dateRange.map((date, idx) => {
                const isToday = date.isSame(dayjs(), 'day');
                const isWeekend = date.day() === 0 || date.day() === 6;

                return (
                  <Box
                    key={idx}
                    sx={{
                      width: columnWidth,
                      minWidth: columnWidth,
                      bgcolor: isToday
                        ? 'rgba(37, 99, 235, 0.1)'
                        : isWeekend
                        ? 'rgba(0,0,0,0.02)'
                        : 'transparent',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                );
              })}
            </Box>

            {/* Today Line */}
            {dayjs().isBetween(startDate, startDate.add(totalDays, 'day'), null, '[]') && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: dayjs().diff(startDate, 'day') * columnWidth,
                  width: 2,
                  bgcolor: 'error.main',
                  zIndex: 2,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -4,
                    left: -4,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                  },
                }}
              />
            )}

            {/* Project Bars */}
            {sortedProjects.map((project, index) => {
              const position = getBarPosition(project);
              if (!position) return null;

              const progress =
                project.status === ProjectStatus.COMPLETED
                  ? 100
                  : (project.completionPercentage ?? 0);

              return (
                <Box
                  key={project.id}
                  sx={{
                    height: 48,
                    position: 'relative',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {/* Project Bar */}
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {project.name}
                        </Typography>
                        <Typography variant="caption">
                          {dayjs(project.startDate).format('MMM D')} -{' '}
                          {dayjs(project.endDate).format('MMM D, YYYY')}
                        </Typography>
                        <br />
                        <Typography variant="caption">
                          Progress: {progress}%
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      onClick={() => handleProjectClick(project.id)}
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: position.left,
                        width: position.width,
                        height: 24,
                        bgcolor: `${statusColors[project.status]}30`,
                        border: '1px solid',
                        borderColor: statusColors[project.status],
                        borderRadius: 1,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scaleY(1.1)',
                          boxShadow: `0 2px 8px ${statusColors[project.status]}40`,
                        },
                      }}
                    >
                      {/* Progress Fill */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          bottom: 0,
                          width: `${progress}%`,
                          bgcolor: statusColors[project.status],
                          opacity: 0.5,
                        }}
                      />

                      {/* Label */}
                      {position.width > 60 && (
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            left: 6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontWeight: 500,
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: position.width - 12,
                          }}
                        >
                          {project.name}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>

                  {/* Milestones */}
                  {project.milestones?.map((milestone) => {
                    const milestoneLeft = getMilestonePosition(milestone);
                    if (milestoneLeft === null) return null;

                    return (
                      <Tooltip
                        key={milestone.id}
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {milestone.name}
                            </Typography>
                            <Typography variant="caption">
                              {dayjs(milestone.dueDate).format('MMM D, YYYY')}
                            </Typography>
                            {milestone.isCompleted && (
                              <Typography variant="caption" color="success.main">
                                {' '}
                                ✓ Completed
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: milestoneLeft - 6,
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderBottom: `12px solid ${
                              milestone.isCompleted ? '#16a34a' : '#9333ea'
                            }`,
                            cursor: 'pointer',
                            zIndex: 3,
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectGanttChart;
